import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { LinkedLogo, NamedLink } from '../../../../components';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { useHistory } from 'react-router-dom';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns columns for blocks in footer (1-4)
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks array of social media link configs
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns = 1,
    socialMediaLinks = [],
    slogan,
    appearance,
    copyright,
    blocks = [],
    options,
    linkLogoToExternalSite,
  } = props;
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = mounted && hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : false;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';
  const isArabicDefault = intl.locale.toLowerCase().startsWith('ar');
  // use block builder instead of mapping blocks manually
  const [isArabic, setIsArabic] = useState(isArabicDefault);
  // Initialize all sections as closed
  const [openSections, setOpenSections] = useState({
    0: false,
    1: false,
    2: false,
    3: false,
    4: false,
    5: false
  }); // keyed by index
  const history = useHistory();
  const isRtl = (intl.locale || '').toLowerCase().startsWith('ar');

  // Initialize language state from URL (pathname/query) and intl locale
  useEffect(() => {
    try {
      const loc = history && history.location ? history.location : null;
      const pathname = loc?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
      const search = loc?.search || (typeof window !== 'undefined' ? window.location.search : '');
      const sp = new URLSearchParams(search || '');
      const langQp = sp.get('lang');
      const isAr = (langQp === 'ar') || (pathname === '/p/ar-home') || ((intl?.locale || '').toLowerCase().startsWith('ar'));
      setIsArabic(!!isAr);
    } catch (_) {
      // no-op
    }
  }, [history, intl]);

  // React to global languageChange events as fired by Topbar
  useEffect(() => {
    const onLanguageChange = e => {
      const next = e?.detail?.locale === 'ar' ? 'ar' : 'en';
      setIsArabic(next === 'ar');
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('languageChange', onLanguageChange);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('languageChange', onLanguageChange);
      }
    };
  }, []);

  const handleFooterLanguageChange = language => {
    try {
      localStorage.setItem('selectedLanguage', language);
    } catch (_) { }
    setIsArabic(language === 'ar');
    // Update document direction for immediate UX feedback
    document.documentElement.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', language === 'ar' ? 'ar' : 'en');
    // Keep user on the same page by default; update only `lang` query param.
    // Landing page special-cases:
    // - EN: navigate to '/'
    // - AR: navigate to '/p/ar-home'
    try {
      const loc = history && history.location ? history.location : null;
      const pathname = loc?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
      const search = loc?.search || (typeof window !== 'undefined' ? window.location.search : '');
      const hash = loc?.hash || (typeof window !== 'undefined' ? window.location.hash : '');
      const sp = new URLSearchParams(search || '');
      const isEnglishLanding = pathname === '/';
      const isArabicLanding = pathname === '/p/ar-home';

      let nextPath = pathname;
      if (language === 'en') {
        // English: go to normal landing if currently on Arabic landing
        if (isArabicLanding) nextPath = '/';
        // Prefer clean URLs by removing lang param
        sp.delete('lang');
      } else {
        // Arabic: go to Arabic landing if currently on normal landing
        if (isEnglishLanding) nextPath = '/p/ar-home';
        else sp.set('lang', 'ar');
      }

      const next = { pathname: nextPath, search: sp.toString(), hash, state: loc?.state };
      history && history.push(next);
    } catch (_) {
      try {
        const url = new URL(window.location.href);
        const isEnglishLanding = url.pathname === '/';
        const isArabicLanding = url.pathname === '/p/ar-home';

        if (language === 'en') {
          url.searchParams.delete('lang');
          if (isArabicLanding) url.pathname = '/';
        } else {
          if (isEnglishLanding) url.pathname = '/p/ar-home';
          else url.searchParams.set('lang', 'ar');
        }
        window.history.replaceState(window.history.state || {}, '', url.toString());
      } catch (__) { }
    }
    // Notify app (DynamicIntlProvider) and Topbar to update
    try {
      window.dispatchEvent(
        new CustomEvent('languageChange', { detail: { locale: language === 'ar' ? 'ar' : 'en' } })
      );
    } catch (_) { }
  };

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 660 : false
  );

  // Handle window resize
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 660);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSection = idx => {
    if (!isMobile) return; // only accordion on mobile <= 660px
    setOpenSections(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const draftId = '00000000-0000-0000-0000-000000000000';

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footerWrapper}>
        <div className={css.innerWrapper}>
          <div className={css.contentBox}>
            <div className={css.footerLeftSide}>
              <div>
                <div className={css.mainLogo}>
                  <IconCard type="whitelogo" />
                </div>
                <div className={css.subscribeBox}>
                  <input
                    type="text"
                    placeholder={intl.formatMessage({ id: 'Footer.subscribePlaceholder' })}
                  />
                  <button>
                    <FormattedMessage id="Footer.subscribeButton" />
                  </button>
                </div>
                <div className={css.stayText}>
                  <FormattedMessage id="Footer.subscribeText" />
                </div>
              </div>
              <div className={css.subscribeText}>
                <FormattedMessage id="Footer.tagline" />
              </div>
            </div>
            <div className={css.rightSide}>
              <div className={css.mainLinks}>
                <div className={classNames(css.linksCol, { [css.open]: openSections[0] })}>
                  <div className={css.linksHeading} onClick={() => toggleSection(0)}>
                    <span><FormattedMessage id="Footer.platform" /></span>
                    <span className={css.accordionIcon}>{openSections[0] ? <IconCard type="minus" /> : <IconCard type="plus" />}</span>
                  </div>
                  <ul>
                    <li>
                      <NamedLink name="LandingPage"><FormattedMessage id="Footer.platform.home" /></NamedLink>
                    </li>
                    <li>
                      <NamedLink name="CMSPage" params={{ pageId: 'how-it-works' }} className={css.topbarLink}>
                        <FormattedMessage id="Footer.platform.howItWorks" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink name="SearchPage"><FormattedMessage id="Footer.platform.findTutor" /></NamedLink>
                    </li>
                    <li>
                      <NamedLink name="CMSPage" params={{ pageId: isArabic ? 'ar-live-classes' : 'live-classes' }} className={css.topbarLink}>
                        <FormattedMessage id="Footer.platform.liveClasses" />
                      </NamedLink>
                    </li>
                    <li>
                      {/* <NamedLink 
                        name= 'EditListingPage'
                        params={{ slug: 'draft', id: draftId, type: 'new', tab: 'details' }}
                      >
                        <FormattedMessage id="Footer.platform.becomeTutor" />
                      </NamedLink> */}
                      <a href='/p/become-a-tutor'><FormattedMessage id="Footer.platform.becomeTutor" /></a>
                    </li>
                    <li>
                      <NamedLink name="CMSPage" params={{ pageId: isArabic ? 'ar-tutor-onboarding' : 'tutor-onboarding' }} className={css.topbarLink}>
                        <FormattedMessage id="Footer.platform.tutorOnboarding" />
                      </NamedLink>
                    </li>
                  </ul>
                </div>
                <div className={classNames(css.linksCol, { [css.open]: openSections[1] })}>
                  <div className={css.linksHeading} onClick={() => toggleSection(1)}>
                    <span><FormattedMessage id="Footer.learning" /></span>
                    <span className={css.accordionIcon}>{openSections[1] ? <IconCard type="minus" /> : <IconCard type="plus" />}</span>
                  </div>
                  <ul>
                    <li>
                      <NamedLink name="CMSPage" params={{ pageId: 'curriculum-overview' }} className={css.topbarLink}>
                        <FormattedMessage id="Footer.learning.curriculum" />
                      </NamedLink>
                    </li>
                    <li>
                      <a href='#'><FormattedMessage id="Footer.learning.levels" /></a>
                    </li>
                    <li>
                      <a href='/p/msa-dialects'><FormattedMessage id="Footer.learning.msaDialects" /></a>
                    </li>
                    <li>
                      <NamedLink name="FaqPage" className={css.topbarLink}>
                        <FormattedMessage id="Footer.learning.faq" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink name="CMSPage" params={{ pageId: isArabic ? 'ar-learning-resources' : 'learning-resources' }} className={css.topbarLink}>
                        <FormattedMessage id="Footer.learning.learningResources" />
                      </NamedLink>
                    </li>
                  </ul>
                </div>
                <div className={classNames(css.linksCol, { [css.open]: openSections[3] })}>
                  <div className={css.linksHeading} onClick={() => toggleSection(3)}>
                    <span><FormattedMessage id="Footer.forParents" /></span>
                    <span className={css.accordionIcon}>{openSections[3] ? <IconCard type="minus" /> : <IconCard type="plus" />}</span>
                  </div>
                  <ul>
                    <li>
                      <a href='#'><FormattedMessage id="Footer.forParents.dashboard" /></a>
                    </li>
                    <li>
                      <a href='#'><FormattedMessage id="Footer.forParents.progress" /></a>
                    </li>
                    <li>
                      <NamedLink name="CMSPage" params={{ pageId: isArabic ? 'ar-safety-privacy' : 'safety-privacy' }} className={css.topbarLink}>
                        <FormattedMessage id="Footer.forParents.safety" />
                      </NamedLink>
                    </li>
                    <li>
                      <a href='/p/testimonials'><FormattedMessage id="Footer.forParents.testimonials" /></a>
                    </li>
                    <li>
                      <a href='/p/track-your-path'><FormattedMessage id="Footer.forParents.trackYourPath" /></a>
                    </li>
                  </ul>
                </div>
                <div className={classNames(css.linksCol, { [css.open]: openSections[2] })}>
                  <div className={css.linksHeading} onClick={() => toggleSection(2)}>
                    <span><FormattedMessage id="Footer.company" /></span>
                    <span className={css.accordionIcon}>{openSections[2] ? <IconCard type="minus" /> : <IconCard type="plus" />}</span>
                  </div>
                  <ul>
                    <li>
                      <NamedLink name="CMSPage" params={{ pageId: isArabic ? 'ar-about' : 'about' }} className={css.topbarLink}>
                        <FormattedMessage id="Footer.company.about" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink name="CMSPage" params={{ pageId: isArabic ? 'ar-our-mission' : 'our-mission' }} className={css.topbarLink}>
                        <FormattedMessage id="Footer.company.mission" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink name="CMSPage" params={{ pageId: isArabic ? 'ar-careers' : 'careers' }} className={css.topbarLink}>
                        <FormattedMessage id="Footer.company.careers" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink name="BlogMainPage" className={css.topbarLink}>
                        <FormattedMessage id="Footer.company.blog" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink name="CMSPage" params={{ pageId: isArabic ? 'ar-press-media' : 'press-media' }} className={css.topbarLink}>
                        <FormattedMessage id="Footer.company.pressMedia" />
                      </NamedLink>
                    </li>
                  </ul>
                </div>
                <div className={classNames(css.linksCol, { [css.open]: openSections[4] })}>
                  <div className={css.linksHeading} onClick={() => toggleSection(4)}>
                    <span><FormattedMessage id="Footer.support" /></span>
                    <span className={css.accordionIcon}>{openSections[4] ? <IconCard type="minus" /> : <IconCard type="plus" />}</span>
                  </div>
                  <ul>
                    <li>
                      <a href='#'><FormattedMessage id="Footer.support.helpCenter" /></a>
                    </li>
                    <li>
                      <NamedLink name="CMSPage" params={{ pageId: isArabic ? 'ar-live-chat-support' : 'live-chat-support' }} className={css.topbarLink}>
                        <FormattedMessage id="Footer.support.liveChat" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-contact-us' : 'contact-us' }}
                      >
                        <FormattedMessage id="Footer.support.contact" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-term-and-condition' : 'term-and-condition' }}
                      >
                        <FormattedMessage id="Footer.support.terms" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-privacy-policy' : 'privacy-policy' }}
                      >
                        <FormattedMessage id="Footer.support.privacy" />
                      </NamedLink>
                    </li>
                    <li>
                      <a href='#'><FormattedMessage id="Footer.support.technicalSupport" /></a>
                    </li>
                  </ul>
                </div>
                <div className={classNames(css.linksCol, { [css.open]: openSections[5] })}>
                  <div className={css.linksHeading} onClick={() => toggleSection(5)}>
                    <span><FormattedMessage id="Footer.payments" /></span>
                    <span className={css.accordionIcon}>{openSections[5] ? <IconCard type="minus" /> : <IconCard type="plus" />}</span>
                  </div>
                  <ul>
                    <li>
                      <NamedLink name='SubscriptionPlan'>
                        <FormattedMessage id="Footer.payments.pricing" />
                      </NamedLink>
                    </li>
                    <li>
                      <a href='#'><FormattedMessage id="Footer.payments.referral" /></a>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-gift-cards' : 'gift-cards' }}
                      >
                        <FormattedMessage id="Footer.payments.giftCards" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="WalletPage"
                      >
                        <FormattedMessage id="Footer.payments.walletInfo" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-secure-payments' : 'secure-payments' }}
                      >
                        <FormattedMessage id="Footer.payments.securePayments" />
                      </NamedLink>
                    </li>
                  </ul>
                </div>
              </div>
              <div className={css.footerBottom}>
                <div className={css.mobileText}>
                  <FormattedMessage id="Footer.tagline" />
                </div>
                <div className={css.socialLinks}>
                  <a href='#'>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="20" height="20" rx="10" fill="white" />
                      <path d="M10.7743 15V10.4389H12.3047L12.5343 8.66078H10.7743V7.52573C10.7743 7.0111 10.9167 6.66038 11.6555 6.66038L12.5963 6.66V5.06961C12.4336 5.04847 11.8751 5 11.2251 5C9.86781 5 8.93858 5.82848 8.93858 7.34963V8.66078H7.40356V10.4389H8.93858V15H10.7743Z" fill="#010002" />
                    </svg>
                    <span><FormattedMessage id="Footer.social.facebook" /></span>
                  </a>
                  <a href='#'>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="20" height="20" rx="10" fill="white" />
                      <g clipPath="url(#clip0_551_429)">
                        <path d="M14.9902 7.94003C14.9668 7.40869 14.8809 7.0434 14.7578 6.72687C14.6308 6.39088 14.4355 6.09007 14.1795 5.84001C13.9295 5.58605 13.6267 5.38872 13.2946 5.26372C12.9762 5.14064 12.6128 5.05471 12.0815 5.03129C11.5462 5.00588 11.3762 5 10.0186 5C8.66087 5 8.49093 5.00588 7.95761 5.0293C7.42627 5.05273 7.06098 5.13873 6.74452 5.26174C6.40846 5.38872 6.10765 5.58407 5.85759 5.84001C5.60363 6.09007 5.40637 6.39287 5.2813 6.72496C5.15822 7.0434 5.07229 7.4067 5.04886 7.93804C5.02345 8.47335 5.01758 8.64329 5.01758 10.001C5.01758 11.3587 5.02345 11.5286 5.04688 12.0619C5.07031 12.5933 5.15631 12.9586 5.27939 13.2751C5.40637 13.6111 5.60363 13.9119 5.85759 14.1619C6.10765 14.4159 6.41044 14.6132 6.74254 14.7382C7.06098 14.8613 7.42428 14.9472 7.9557 14.9707C8.48894 14.9942 8.65896 15 10.0166 15C11.3743 15 11.5169 14.9942 12.0776 14.9707C12.6089 14.9472 12.9742 14.8613 13.2907 14.7382C13.9627 14.4784 14.4941 13.9471 14.7539 13.2751C14.8769 12.9566 14.9629 12.5933 14.9863 12.0619C15.0098 11.5286 15.0156 11.3587 15.0156 10.001C15.0156 8.64329 15.0137 8.47335 14.9902 7.94003ZM14.7539 13.2751C14.8769 12.9566 14.9629 12.5933 14.9863 12.0619C15.0098 11.5286 15.0156 11.3587 15.0156 10.001C15.0156 8.64329 15.0137 8.47335 14.9902 7.94003ZM9.00393 11.6014V8.43743L11.7383 10.0195L9.00393 11.6014Z" fill="black" />
                      </g>
                      <defs>
                        <clipPath id="clip0_551_429">
                          <rect width="10" height="10" fill="white" transform="translate(5 5)" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span><FormattedMessage id="Footer.social.instagram" /></span>
                  </a>
                  <a href='#'>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="20" height="20" rx="10" fill="white" />
                      <g clipPath="url(#clip0_551_431)">
                        <path d="M9.99965 13.6327C9.99881 13.6327 9.99789 13.6327 9.99698 13.6327C9.69523 13.6306 7.02769 13.6063 6.27192 13.4019C5.74373 13.2599 5.32679 12.8434 5.18442 12.3152C4.98667 11.5727 4.99926 10.1434 5.00063 10.0292C4.99933 9.91563 4.98659 8.47443 5.18381 7.72591C5.18404 7.72522 5.18419 7.72446 5.18442 7.72377C5.32518 7.20154 5.75152 6.77216 6.27062 6.62979C6.27192 6.62941 6.27329 6.62911 6.27459 6.62872C7.02181 6.43234 9.69462 6.40831 9.99698 6.40625H10.0024C10.3049 6.40831 12.9795 6.43265 13.7284 6.63734C14.2552 6.77895 14.6718 7.1949 14.8146 7.72248C15.0197 8.47153 15.0007 9.91585 14.9987 10.0378C15.0001 10.1579 15.0121 11.5744 14.8155 12.3206C14.8153 12.3213 14.8151 12.322 14.8149 12.3227C14.6725 12.851 14.2556 13.2675 13.7268 13.4096C13.7262 13.4098 13.7254 13.41 13.7247 13.4102C12.9776 13.6065 10.3047 13.6305 10.0024 13.6327C10.0015 13.6327 10.0006 13.6327 9.99965 13.6327ZM5.93897 7.92626C5.76532 8.58696 5.78173 10.0098 5.78188 10.0241V10.0344C5.77669 10.429 5.79493 11.5717 5.93905 12.113C6.00893 12.3721 6.21454 12.5774 6.47532 12.6476C7.03295 12.7984 9.20558 12.8459 9.99965 12.8514C10.7958 12.8459 12.9716 12.7997 13.5251 12.6549C13.7851 12.5845 13.99 12.3799 14.0603 12.1204C14.2046 11.5713 14.2227 10.4341 14.2174 10.042C14.2174 10.0379 14.2174 10.0338 14.2175 10.0297C14.2247 9.63036 14.2105 8.47428 14.061 7.9287C14.0609 7.92816 14.0607 7.92763 14.0606 7.9271C13.9904 7.66678 13.7847 7.46147 13.524 7.39136C12.9718 7.24037 10.7956 7.19307 9.99965 7.1875C9.20405 7.19307 7.03036 7.23915 6.47517 7.38381C6.21935 7.45476 6.00908 7.66739 5.93897 7.92626ZM9.00393 11.6014V8.43743L11.7383 10.0195L9.00393 11.6014Z" fill="black" />
                      </g>
                      <defs>
                        <clipPath id="clip0_551_431">
                          <rect width="10" height="10" fill="white" transform="translate(5 5)" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span><FormattedMessage id="Footer.social.youtube" /></span>
                  </a>
                </div>
                <div className={css.languageButton}>
                  <div className={css.toggleWrapper}>
                    <span className={`${css.label} ${isArabic ? css.active : ""}`}>
                      AR
                    </span>
                    <div
                      className={`${css.toggle} ${isRtl ? (isArabic ? css.right : css.left) : (isArabic ? css.left : css.right)}`}
                      onClick={() => handleFooterLanguageChange(isArabic ? 'en' : 'ar')}
                    >
                      <div className={css.circle}></div>
                    </div>
                    <span className={`${css.label} ${!isArabic ? css.active : ""}`}>
                      EN
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={css.footerBottomBox}>
            <div className={css.copyRightText}>
              <FormattedMessage id="Footer.copyright" />
            </div>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
